import React, { RefObject } from 'react';
import { AdacText } from '@adac-de/component-library';
import type { UpdateAnswers } from '../../types';
import styles from './question-card.module.scss';
import { MultiQuestionHouseCard } from './multi-question-house-card';
import { SelectButtonContainer, SelectButton } from '@components/select-button/select-button';
import { Select } from '@components/select/select';
import { WizardButtons } from '@components/wizard-buttons/wizard-buttons';
import { mapSectionKey } from '@utils/tracking/tracking';
import { WizardState } from '@hooks/utils/wizard-types';

export type QuestionCardProps = {
  activeQuestion: PvWizardQuestion;
  onAnswerUpdated: UpdateAnswers;
  nextStep: () => void;
  previousStep: () => void;
  showNextButton?: boolean;
  showBackButton?: boolean;
  wizardState: WizardState;
  sectionKey: String;
  formModals?: {
    roofAge: PvWizardContent['disclaimerText'];
    roofMaterial: PvWizardContent['disclaimerText'];
  };
  modalRef?: RefObject<HTMLDivElement>;
};

const multiQuestionKey = 'roofDetails';

export const QuestionCard: React.FC<QuestionCardProps> = ({
  activeQuestion,
  onAnswerUpdated,
  nextStep,
  previousStep,
  showNextButton = false,
  showBackButton = false,
  wizardState,
  sectionKey,
  formModals,
  modalRef
}) =>
  activeQuestion.__typename === 'PvWizardMultiQuestionType' ? (
    <MultiQuestionHouseCard
      sectionKey={sectionKey}
      activeQuestion={activeQuestion}
      onAnswerUpdated={onAnswerUpdated}
      previousStep={previousStep}
      nextStep={nextStep}
      wizardState={wizardState}
      showBackButton={sectionKey !== 'startScreen'}
      formModals={formModals}
      modalRef={modalRef}
    />
  ) : (
    <div className={styles.wrapper} data-testid={`slider-${sectionKey}`}>
      <div
        className={styles.card}
        key={activeQuestion.key}
        data-testid={`${sectionKey}-${wizardState.activeSlide + 1}`}
      >
        {activeQuestion.headline && (
          <AdacText
            type="normal"
            bold
            className="margin-left--xs margin-bottom--s"
            data-testid="slide-headline"
            aria-label={`${sectionKey}-${wizardState.activeSlide + 1}`}
          >
            {activeQuestion.headline} <span className="screen-reader-only">erforderlich</span>
          </AdacText>
        )}
        {activeQuestion.__typename === 'PvWizardQuestionTypeDropdown' && (
          <Select
            id={activeQuestion.key}
            tabIndex={1}
            selectValue={wizardState.answers[activeQuestion.key] as string}
            handleOnChange={event => onAnswerUpdated({ [activeQuestion.key]: event.target.value })}
            defaultOptionText={activeQuestion.defaultOptionText}
            options={activeQuestion.answerOptions}
            ariaLabel={`${sectionKey}-${wizardState.activeSlide + 1}`}
          />
        )}
        {activeQuestion.__typename === 'PvWizardQuestionTypeButton' && (
          <SelectButtonContainer>
            {activeQuestion.answerOptions.map(option => (
              <SelectButton
                tabIndex={1}
                key={option.title}
                title={option.title}
                subline={option.subline}
                icon={option.icon}
                value={option.value}
                handleOnClick={value => onAnswerUpdated({ [activeQuestion.key]: value })}
                isSelected={wizardState.answers[activeQuestion.key] === option.value}
                ariaLabel={`${sectionKey}-${wizardState.activeSlide + 1}`}
              />
            ))}
          </SelectButtonContainer>
        )}
      </div>
      <WizardButtons
        showBackButton={showBackButton}
        showNextButton={showNextButton}
        onClickBack={previousStep}
        onClickNext={nextStep}
        trackingNextButton={
          wizardState.activeSlideKey !== multiQuestionKey
            ? {
                click: {
                  label: 'Weiter',
                  element: mapSectionKey[wizardState.activeSlideKey].label,
                  funnelStep: mapSectionKey[wizardState.activeSlideKey].funnelStep
                }
              }
            : undefined
        }
      />
    </div>
  );
